
import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../../App';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { CHATS_COLLECTION } from '../../config/constants';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import { ArrowLeft, User, Star, TrendingUp, Award, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ClientChatCard from '../../components/chat/ClientChatCard.jsx';
import { getRankData } from '../../components/client/ServiDetailModal.jsx';

const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const ClientesPorPeriodoScreen = () => {
    const { db, user, fullUserData } = useAppContext();
    const [finishedServices, setFinishedServices] = useState([]);
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    useEffect(() => {
        if (!db || !user) {
            setIsLoading(false);
            return () => {};
        }

        const q = query(
            collection(db, CHATS_COLLECTION),
            where('participants', 'array-contains', user.uid),
            where('status', '==', 'Finalizado'),
            orderBy('lastMessageTimestamp', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setFinishedServices(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
            setIsLoading(false);
        }, (error) => {
            console.error("Erro ao buscar serviços:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, user]);

    const filteredByPeriod = useMemo(() => {
        return finishedServices.filter(service => {
            const date = service.lastMessageTimestamp?.toDate ? service.lastMessageTimestamp.toDate() : null;
            if (!date) return false;
            return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
        });
    }, [finishedServices, selectedMonth, selectedYear]);

    const stats = useMemo(() => {
        const total = filteredByPeriod.length;
        const totalRating = filteredByPeriod.reduce((acc, curr) => acc + (curr.clientRating || 0), 0);
        const ratedCount = filteredByPeriod.filter(s => s.clientRating).length;
        const averageRating = ratedCount > 0 ? (totalRating / ratedCount).toFixed(1) : "0.0";

        return { total, averageRating };
    }, [filteredByPeriod]);

    const uniqueClients = Array.from(new Map(filteredByPeriod.map(item => [item.clientId, item])).values());

    // Anos disponíveis para filtro (Inicia em 2025 até o ano atual + anos no banco)
    const years = useMemo(() => {
        const startYear = 2025;
        const yearSet = new Set();

        // Garante o intervalo de 2025 até o ano atual
        for (let y = startYear; y <= currentYear; y++) {
            yearSet.add(y);
        }

        // Adiciona anos que por ventura existam nos dados e estejam fora do range
        finishedServices.forEach(s => {
            const y = s.lastMessageTimestamp?.toDate ? s.lastMessageTimestamp.toDate().getFullYear() : null;
            if (y) yearSet.add(y);
        });

        return Array.from(yearSet).sort((a, b) => b - a);
    }, [finishedServices, currentYear]);

    const profile = fullUserData?.profile_servi || {};
    const rank = getRankData(profile.completedServices || 0);

    if (isLoading) {
        return <AuthLoadingScreen message="Buscando clientes..." />;
    }

    return (
        <div className="p-4 h-full overflow-y-auto bg-gray-50 pb-20">
            <div className="flex items-center mb-6">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-2"><ArrowLeft className="w-6 h-6 text-gray-700" /></button>
                <h1 className="text-2xl font-bold text-gray-800">Fluxo de Clientes</h1>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center text-blue-500 mb-1">
                        <Users size={16} className="mr-2" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Atendimentos</span>
                    </div>
                    <p className="text-2xl font-black text-gray-800">{stats.total}</p>
                    <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">Em {months[selectedMonth]}</p>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center text-yellow-500 mb-1">
                        <Star size={16} className="mr-2" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Média Mês</span>
                    </div>
                    <p className="text-2xl font-black text-gray-800">{stats.averageRating}</p>
                    <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">Nível de Satisfação</p>
                </div>
            </div>

            <div className={`p-4 rounded-2xl shadow-md border mb-6 flex items-center justify-between ${rank.bg}`}>
                <div className="flex items-center">
                    <div className="bg-white p-2 rounded-xl mr-3 shadow-sm">
                        <Award className={rank.text} size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black opacity-60 uppercase tracking-widest">Status da Conta</p>
                        <h3 className={`text-lg font-black uppercase ${rank.text}`}>{rank.label}</h3>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-black opacity-60 uppercase tracking-widest text-gray-500">Total Geral</p>
                    <p className="text-xl font-black text-gray-800">{profile.completedServices || 0}</p>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                    <label htmlFor="client-month-select" className="sr-only">Selecionar Mês</label>
                    <select id="client-month-select" name="client-month-select" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="p-3 border-2 rounded-xl bg-white font-bold text-gray-700 w-full focus:border-blue-500 outline-none">{months.map((m, i) => <option key={m} value={i}>{m}</option>)}</select>
                </div>
                <div>
                    <label htmlFor="client-year-select" className="sr-only">Selecionar Ano</label>
                    <select id="client-year-select" name="client-year-select" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="p-3 border-2 rounded-xl bg-white font-bold text-gray-700 w-full focus:border-blue-500 outline-none">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
                </div>
            </div>

            <div className="bg-white p-4 rounded-[2rem] shadow-lg border border-gray-100">
                <div className="flex items-center justify-between mb-4 px-2">
                    <h2 className="text-sm font-black text-gray-400 uppercase tracking-widest flex items-center">
                        <User className="w-4 h-4 mr-2" /> Clientes do Período
                    </h2>
                    <span className="bg-gray-100 text-gray-500 text-[10px] font-black px-2 py-1 rounded-full">{uniqueClients.length} ÚNICOS</span>
                </div>

                <div className="space-y-3">
                    {uniqueClients.length > 0 ? (
                        uniqueClients.map(client => <ClientChatCard key={client.id} chat={client} userRole='servi' onClick={() => navigate(`/servi/chat/${client.id}`)} />)
                    ) : (
                        <div className="text-center py-12">
                            <TrendingUp className="w-12 h-12 text-gray-100 mx-auto mb-2" />
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">Nenhum atendimento registrado em {months[selectedMonth]}.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientesPorPeriodoScreen;
